import { useState } from "react";
import { useNavigate } from "react-router";
import ComponentCard from "../common/ComponentCard";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import Select from "../../components/form/Select";
import Switch from "../../components/form/switch/Switch";
import Button from "../ui/button/Button";
import axios from "axios";
import { authService } from "../../services/authService";

interface CoproducerFormData {
    productParticipantId: string;
    productId: string;
    type: string;
    email: string;
    percentage: number;
    contractDuration: number;
    receivesProducerSales: boolean;
    receivesAffiliateSales: boolean;
    nfeSplit: boolean;
    status: string;
    createdAt: string;
    updatedAt: string;
}

interface NewCoproducerFormProps {
    productId?: string;
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
}

export default function NewCoproducerForm({ productId, onSuccess, onError }: NewCoproducerFormProps) {
    const [formData, setFormData] = useState<CoproducerFormData>({
        productParticipantId: "",
        productId: productId || "",
        type: "coproducer",
        email: "",
        percentage: 0,
        contractDuration: 30, // Definindo um valor padrão
        receivesProducerSales: true, // Iniciando marcado
        receivesAffiliateSales: true, // Iniciando marcado
        nfeSplit: false,
        status: "pending",
        createdAt: "",
        updatedAt: ""
    });

    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");

    const contratDuration = [
        { value: "30", label: "30 dias" },
        { value: "60", label: "60 dias" },
        { value: "90", label: "90 dias" },
        { value: "180", label: "180 dias" },
        { value: "365", label: "365 dias" },
        { value: "0", label: "Eterno" }
    ];

    // Handler para o e-mail do co-produtor
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        setFormData({
            ...formData,
            email: value  // Atualiza também o campo email
        });

        // Limpar erros ao digitar
        if (errors.email && value) {
            setErrors({
                ...errors,
                email: ""
            });
        }

        // Limpar mensagens de API
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    // Handler para o select de duração do contrato
    const handleContractDurationChange = (value: string) => {
        setFormData({
            ...formData,
            contractDuration: parseInt(value, 10) || 0
        });

        // Limpar erros ao selecionar
        if (errors.contractDuration && value) {
            setErrors({
                ...errors,
                contractDuration: ""
            });
        }

        // Limpar mensagens de API
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    // Handler para o switch de receber vendas do produtor
    const handleReceivesProducerSalesChange = (checked: boolean) => {
        setFormData({
            ...formData,
            receivesProducerSales: checked
        });

        // Limpar mensagens de API
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    // Handler para o switch de receber vendas dos afiliados
    const handleReceivesAffiliateSalesChange = (checked: boolean) => {
        setFormData({
            ...formData,
            receivesAffiliateSales: checked
        });

        // Limpar mensagens de API
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    // Handler para o switch de divisão de responsabilidade fiscal
    const handleNfeSplitChange = (checked: boolean) => {
        setFormData({
            ...formData,
            nfeSplit: checked
        });

        // Limpar mensagens de API
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    // Handler para o input de percentagem
    const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        const numValue = parseFloat(value) || 0;

        setFormData({
            ...formData,
            percentage: numValue
        });

        // Limpar erros ao digitar
        if (errors.percentage && numValue > 0) {
            setErrors({
                ...errors,
                percentage: ""
            });
        }

        // Limpar mensagens de API
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const handleGoBack = () => {
        navigate(`/products/edit/${productId}?tab=coproduction`);
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.email) {
            newErrors.email = "O e-mail do co-produtor é obrigatório";
        }

        if (!formData.percentage || formData.percentage <= 0) {
            newErrors.percentage = "A porcentagem de participação é obrigatória e deve ser maior que zero";
        }

        // O contractDuration pode ser 0 para contratos eternos, então só validamos se está definido
        if (formData.contractDuration === undefined) {
            newErrors.contractDuration = "A duração do contrato é obrigatória";
        }

        setErrors(newErrors);

        // Se não houver erros, retorna true
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }

        // Validar formulário
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setApiError("");
        setApiSuccess("");

        try {
            // Preparar dados para API
            const coproducerData = {
                productId: formData.productId,
                coproducerEmail: formData.email,
                type: formData.type,
                percentage: formData.percentage,
                contractDuration: Number(formData.contractDuration), // Garantir que seja enviado como número
                receivesProducerSales: formData.receivesProducerSales,
                receivesAffiliateSales: formData.receivesAffiliateSales,
                nfeSplit: formData.nfeSplit,
                status: 'pending'
            };

            // Criar nova co-produtor
            const response = await axios.post(
                `${import.meta.env.VITE_API_URL}/api/product-participants`,
                coproducerData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            if (response.status === 201) {
                setApiSuccess("Co-produtor convidado com sucesso!");

                // Callback de sucesso se fornecido
                if (onSuccess) {
                    onSuccess(response.data);
                }
            }
        } catch (error: any) {
            console.error("Erro ao convidar co-produtor:", error);
            setApiError(error.response?.data?.error || "Erro ao convidar o co-produtor. Tente novamente.");

            // Callback de erro se fornecido
            if (onError) {
                onError(error);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-1">
                <ComponentCard title="Adicionar Co-Produtor">
                    <>
                        {apiError && (
                            <div className="bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4">
                                {apiError}
                            </div>
                        )}

                        {apiSuccess && (
                            <div className="bg-green-50 dark:bg-green-900/10 text-green-600 dark:text-green-400 p-3 rounded-lg mb-4">
                                {apiSuccess}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <div className="mt-5">
                                <Label htmlFor="inputName">E-mail do co-produtor *</Label>
                                <Input
                                    type="email"
                                    id="inputName"
                                    placeholder="Informe o e-mail do co-produtor"
                                    onChange={handleEmailChange}
                                    error={!!errors.email}
                                    hint={errors.email}
                                />
                            </div>

                            <div className="mt-5">
                                <Label>Duração do contrato *</Label>
                                <Select
                                    options={contratDuration}
                                    placeholder="Selecione uma opção"
                                    onChange={handleContractDurationChange}
                                    value={String(formData.contractDuration)}
                                    className={errors.contractDuration ? "border-red-500" : ""}
                                />
                                {errors.contractDuration && <p className="mt-1 text-sm text-red-500">{errors.contractDuration}</p>}
                            </div>

                            <div className="mt-6">
                                <Switch
                                    label="Receber vendas do produtor"
                                    checked={formData.receivesProducerSales}
                                    onChange={handleReceivesProducerSalesChange}
                                />
                            </div>

                            <div className="mt-6">
                                <Switch
                                    label="Receber vendas dos afiliados"
                                    checked={formData.receivesAffiliateSales}
                                    onChange={handleReceivesAffiliateSalesChange}
                                />
                            </div>

                            <div className="mt-6">
                                <Switch
                                    label="Dividir responsabilidade de emissão das notas fiscais com esse co-produtor? (modelo parceria de negócios"
                                    checked={formData.nfeSplit}
                                    onChange={handleNfeSplitChange}
                                />
                            </div>

                            <div className="mt-5">
                                <Label htmlFor="inputPrice">Comissão (%) *</Label>
                                <Input
                                    type="number"
                                    id="inputPrice"
                                    placeholder="0"
                                    min="0"
                                    max="100"
                                    onChange={handlePercentageChange}
                                    value={formData.percentage.toString()}
                                    error={!!errors.percentage}
                                    hint={errors.percentage}
                                />
                            </div>

                            <div className="mt-8 flex justify-between gap-4.5">
                                <Button
                                    variant="outline"
                                    size="md"
                                    onClick={handleGoBack}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    size="md"
                                    variant="primary"
                                    onClick={() => handleSubmit()}
                                    disabled={loading}
                                >
                                    {loading ? "Enviando convite..." : "Convidar"}
                                </Button>
                            </div>
                        </form>
                    </>
                </ComponentCard>
            </div>
        </div>
    );
}
